import React from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Chip, Stack, Typography, alpha, useTheme } from "@mui/material";

import { pulse } from "./job-detail-constants";
import { STATUS_CHIP_COLOR } from "./job-detail-helpers";

export interface ServiceListItem {
  name: string;
  type: string;
  status: string;
  message: string;
}

export interface JobDetailMetadataProps {
  step: string;
  pipelineRunId?: number | string;
  pipelineRunStatus?: string;
  pipelineRunConclusion?: string | null;
  pipelineRunHtmlUrl?: string;
  isGroupNode: boolean;
  activeSvc: string | null;
  services?: ServiceListItem[];
  onServiceSelect?: (service: string | null) => void;
}

export function JobDetailMetadata({
  step,
  pipelineRunId,
  pipelineRunStatus,
  pipelineRunConclusion,
  pipelineRunHtmlUrl,
  isGroupNode,
  activeSvc,
  services,
  onServiceSelect,
}: JobDetailMetadataProps): React.ReactElement {
  const theme = useTheme();

  return (
    <>
      {/* ── Pipeline Run Info (CI run details) ── */}
      {step === "build" && pipelineRunId && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
          >
            Pipeline Run
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              label={`Run ${pipelineRunId}`}
              size="small"
              variant="outlined"
              sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
            />
            {pipelineRunStatus && (
              <Chip
                label={pipelineRunStatus}
                size="small"
                color={
                  pipelineRunStatus === "completed"
                    ? "success"
                    : pipelineRunStatus === "failed"
                      ? "error"
                      : "info"
                }
                sx={{ fontSize: "0.7rem" }}
              />
            )}
            {pipelineRunConclusion && (
              <Chip
                label={pipelineRunConclusion}
                size="small"
                variant="outlined"
                color={
                  pipelineRunConclusion === "success" ? "success" : "error"
                }
                sx={{ fontSize: "0.7rem" }}
              />
            )}
            {pipelineRunHtmlUrl && (
              <Typography
                variant="caption"
                component="a"
                href={pipelineRunHtmlUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main", textDecoration: "underline" }}
              >
                View run
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {/* ── LEVEL 1: Service list (group node, no service selected) ── */}
      {isGroupNode && !activeSvc && services && services.length > 0 && (
        <Box sx={{ p: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block", fontWeight: 600 }}
          >
            Services ({services.length})
          </Typography>
          <Stack spacing={0.5}>
            {services.map((svc) => {
              const svcColor =
                svc.status === "failed"
                  ? theme.palette.error.main
                  : svc.status === "in_progress"
                    ? theme.palette.info.main
                    : svc.status === "completed"
                      ? theme.palette.success.main
                      : theme.palette.grey[400];
              return (
                <Stack
                  key={svc.name}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={() => onServiceSelect?.(svc.name)}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    cursor: "pointer",
                    border: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: svcColor,
                      flexShrink: 0,
                      ...(svc.status === "in_progress" && {
                        animation: `${pulse} 1.5s infinite`,
                      }),
                    }}
                  />
                  <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                    {svc.name}
                  </Typography>
                  <Chip
                    label={svc.status}
                    size="small"
                    color={STATUS_CHIP_COLOR[svc.status] || "default"}
                    sx={{ height: 20, fontSize: "0.68rem" }}
                  />
                  {svc.message && svc.message !== svc.status && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: 200 }}
                    >
                      {svc.message}
                    </Typography>
                  )}
                  <ChevronRightIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}
    </>
  );
}
